/**
 * useEntityDisplayNames — plural counterpart of {@link useEntityDisplayName}:
 * resolve a set of entities' display names from the store, fetching only the
 * name properties for the ones that aren't cached yet.
 *
 * Use it where a PARENT resolves names for a list of presentational children
 * (e.g. the proposal header's trigger context chips/menu), so the leaves need
 * no hooks of their own. Like the singular hook it reads summary entries (the
 * shape `ensureEntityName` writes) and stays deliberately name-only — never
 * a full-object hydration for a label.
 *
 * Subscription/aliasing semantics match the singular hook: one GLOBAL store
 * subscription (a merged entity's name lands under its canonical post-merge
 * id, which only fires a global change), ids resolved through the merge-alias
 * map on every read. Unresolved ids are requested via `ensureEntityName`;
 * `entityNameBatcher` coalesces a render tick's worth of ids into one
 * request.
 *
 * @param entityIds - Entity UUIDs to resolve. Undefined entries are ignored.
 * @param orgId - Organization ID for fetches. Undefined/<=0 → no fetches.
 * @returns Map from requested entityId → fresh display name. Ids without a
 *   resolved name are absent. The Map reference is stable while its contents
 *   are unchanged.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  ensureEntityName,
  getNameFailureVersion,
  subscribeToNameFetchFailures,
} from '@/lib/crm/object-cards/entityNameBatcher';
import {
  getStoredObject,
  resolveEntityId,
  subscribe as subscribeToStore,
} from '@/lib/crm/store';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';

const NOOP_SUBSCRIBE = (_cb: () => void) => () => {};

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();

/** Store-first name for one id, or undefined while unresolved. */
function readName(entityId: string): string | undefined {
  const stored = getStoredObject(resolveEntityId(entityId));
  if (!stored?.data) {
    return undefined;
  }
  const name = getDisplayName(stored.data, '');
  return name === '' ? undefined : name;
}

export function useEntityDisplayNames(
  entityIds: readonly (string | undefined)[],
  orgId: number | undefined
): ReadonlyMap<string, string> {
  // Stable string key from the sorted+deduped ids so downstream memos don't
  // depend on the array reference (the useCrmObjectSnapshots idiom).
  const stableKey = useMemo(() => {
    const defined = entityIds.filter((id): id is string => Boolean(id));
    return [...new Set(defined)].sort().join('\0');
  }, [entityIds]);
  const dedupedIds = useMemo(
    () => (stableKey ? stableKey.split('\0') : []),
    [stableKey]
  );

  const subscribe = useCallback(
    (cb: () => void) =>
      dedupedIds.length > 0 ? subscribeToStore(cb) : NOOP_SUBSCRIBE(cb),
    [dedupedIds]
  );
  // Snapshot as a primitive fingerprint (id\x1fname pairs, pairs joined by
  // \0) so useSyncExternalStore's equality check is trivial and the Map
  // below is rebuilt only when a name actually changes. \x1f (ASCII Unit
  // Separator) marks the id/name boundary within a pair — distinct from the
  // \0 pair separator — and can't collide with a UUID id or a display name.
  const getSnapshot = useCallback(
    () => dedupedIds.map(id => `${id}\x1f${readName(id) ?? ''}`).join('\0'),
    [dedupedIds]
  );
  const fingerprint = useSyncExternalStore(subscribe, getSnapshot);

  // Re-render when the name-fetch failure set changes so callers relying on
  // fallback labels settle instead of waiting forever.
  useSyncExternalStore(subscribeToNameFetchFailures, getNameFailureVersion);

  const names = useMemo(() => {
    if (!fingerprint) {
      return EMPTY_MAP;
    }
    const map = new Map<string, string>();
    for (const pair of fingerprint.split('\0')) {
      const [id, name] = pair.split('\x1f');
      if (name !== '') {
        map.set(id, name);
      }
    }
    return map;
  }, [fingerprint]);

  useEffect(() => {
    if (!orgId || orgId <= 0) {
      return;
    }
    for (const id of dedupedIds) {
      if (!names.has(id)) {
        // entityNameBatcher coalesces these into one request per tick.
        ensureEntityName(orgId, id);
      }
    }
  }, [orgId, dedupedIds, names]);

  return names;
}
